/**
 * Neon white Challenges roulette overlay (mode selection + Dots reward).
 */

export type ChallengeRouletteSegment = {
  label: string;
};

type RouletteRun = {
  segments: ChallengeRouletteSegment[];
  resultIndex: number;
  durationMs: number;
  onDone: () => void;
  onTick?: () => void;
  onStop?: () => void;
};

let overlay: HTMLDivElement | null = null;
let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let labelEl: HTMLDivElement | null = null;
let raf = 0;
let running: RouletteRun | null = null;
let startAt = 0;
let appearScale = 0;
let lastTickAt = 0;
let stopFired = false;

function ensureDom() {
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "challengeRouletteOverlay";
  overlay.className = "challengeRouletteOverlay hidden";
  overlay.setAttribute("aria-hidden", "true");

  canvas = document.createElement("canvas");
  canvas.className = "challengeRouletteCanvas";
  canvas.width = 420;
  canvas.height = 420;
  ctx = canvas.getContext("2d");

  labelEl = document.createElement("div");
  labelEl.className = "challengeRouletteLabel hidden";

  overlay.appendChild(canvas);
  overlay.appendChild(labelEl);
  document.body.appendChild(overlay);
}

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

function fitSegmentFont(
  c: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  baseSize: number,
): number {
  let size = Math.max(7, Math.floor(baseSize));
  const fontFamily = `"Segoe UI", system-ui, sans-serif`;
  while (size > 7) {
    c.font = `bold ${size}px ${fontFamily}`;
    if (c.measureText(text).width <= maxWidth) break;
    size -= 1;
  }
  c.font = `bold ${size}px ${fontFamily}`;
  return size;
}

function drawWheel(angle: number, segments: ChallengeRouletteSegment[], highlightIndex: number | null) {
  if (!ctx || !canvas) return;
  const w = canvas.width;
  const h = canvas.height;
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) * 0.42;
  ctx.clearRect(0, 0, w, h);

  const n = Math.max(1, segments.length);
  const slice = (Math.PI * 2) / n;
  const labelRadius = r * 0.58;
  // Chord width at label radius, with padding so text stays inside the wedge.
  const maxLabelWidth = Math.max(10, 2 * labelRadius * Math.sin(slice / 2) * 0.82);
  const baseFont = Math.max(10, Math.floor(26 - n * 0.35));

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(angle);

  for (let i = 0; i < n; i++) {
    const a0 = i * slice - Math.PI / 2;
    const a1 = a0 + slice;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, r, a0, a1);
    ctx.closePath();
    const lit = highlightIndex === i;
    ctx.fillStyle = lit ? "rgba(255,255,255,0.22)" : i % 2 === 0 ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)";
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.85)";
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.save();
    ctx.rotate(a0 + slice / 2);
    ctx.fillStyle = "#ffffff";
    ctx.shadowColor = "rgba(255,255,255,0.9)";
    ctx.shadowBlur = 12;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const text = segments[i]?.label ?? "";
    fitSegmentFont(ctx, text, maxLabelWidth, baseFont);
    ctx.fillText(text, labelRadius, 0, maxLabelWidth);
    ctx.restore();
  }

  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.lineWidth = 3;
  ctx.shadowColor = "rgba(180,230,255,0.95)";
  ctx.shadowBlur = 18;
  ctx.stroke();
  ctx.restore();

  // Pointer at top
  ctx.save();
  ctx.translate(cx, cy - r - 6);
  ctx.beginPath();
  ctx.moveTo(0, 14);
  ctx.lineTo(-10, -4);
  ctx.lineTo(10, -4);
  ctx.closePath();
  ctx.fillStyle = "#ffffff";
  ctx.shadowColor = "rgba(255,255,255,0.9)";
  ctx.shadowBlur = 10;
  ctx.fill();
  ctx.restore();
}

function tick(now: number) {
  if (!running || !overlay || !canvas) return;
  const { segments, resultIndex, durationMs, onDone, onTick, onStop } = running;
  const n = Math.max(1, segments.length);
  const elapsed = now - startAt;
  const appearT = Math.min(1, elapsed / 450);
  appearScale = 0.35 + easeOutCubic(appearT) * 0.65;
  canvas.style.transform = `scale(${appearScale})`;
  canvas.style.opacity = String(Math.min(1, appearT * 1.4));

  const spinT = Math.min(1, elapsed / durationMs);
  const turns = 4 + resultIndex / n;
  // Pointer is at top (-PI/2). Segment i center should land under pointer.
  const slice = (Math.PI * 2) / n;
  const targetAngle = -resultIndex * slice - slice / 2;
  const angle = targetAngle + (1 - easeOutCubic(spinT)) * turns * Math.PI * 2;

  drawWheel(angle, segments, spinT > 0.98 ? resultIndex : null);

  if (spinT < 0.98 && now - lastTickAt >= 90 + spinT * 140) {
    lastTickAt = now;
    onTick?.();
  }

  if (spinT >= 1) {
    if (!stopFired) {
      stopFired = true;
      onStop?.();
    }
    const done = onDone;
    running = null;
    raf = 0;
    // brief hold then fade
    overlay.style.transition = "opacity 0.45s ease";
    overlay.style.opacity = "0";
    window.setTimeout(() => {
      hideChallengeRoulette();
      done();
    }, 480);
    return;
  }
  raf = requestAnimationFrame(tick);
}

export function hideChallengeRoulette() {
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
  running = null;
  if (overlay) {
    overlay.classList.add("hidden");
    overlay.style.opacity = "";
    overlay.style.transition = "";
    overlay.setAttribute("aria-hidden", "true");
  }
  if (canvas) {
    canvas.style.transform = "";
    canvas.style.opacity = "";
  }
  if (labelEl) {
    labelEl.classList.add("hidden");
    labelEl.textContent = "";
  }
}

export function playChallengeRoulette(opts: {
  segments: ChallengeRouletteSegment[];
  resultIndex: number;
  durationMs?: number;
  onTick?: () => void;
  onStop?: () => void;
}): Promise<void> {
  ensureDom();
  hideChallengeRoulette();
  if (!overlay || !canvas || !labelEl) return Promise.resolve();
  labelEl.classList.add("hidden");
  overlay.classList.remove("hidden");
  overlay.style.opacity = "1";
  overlay.setAttribute("aria-hidden", "false");
  canvas.classList.remove("hidden");
  appearScale = 0.35;
  canvas.style.transform = "scale(0.35)";
  canvas.style.opacity = "0";
  startAt = performance.now();
  lastTickAt = 0;
  stopFired = false;
  return new Promise((resolve) => {
    running = {
      segments: opts.segments.length ? opts.segments : [{ label: "?" }],
      resultIndex: Math.max(0, Math.min(opts.resultIndex, Math.max(0, opts.segments.length - 1))),
      durationMs: opts.durationMs ?? 5000,
      onDone: () => resolve(),
      onTick: opts.onTick,
      onStop: opts.onStop,
    };
    raf = requestAnimationFrame(tick);
  });
}

export function showChallengeRevealText(lines: string[], holdMs = 1600): Promise<void> {
  ensureDom();
  if (!overlay || !labelEl || !canvas) return Promise.resolve();
  canvas.classList.add("hidden");
  overlay.classList.remove("hidden");
  overlay.style.opacity = "1";
  overlay.setAttribute("aria-hidden", "false");
  labelEl.classList.remove("hidden");
  labelEl.innerHTML = lines
    .map((l, i) => `<div class="challengeRevealLine${i === 0 ? " challengeRevealLine--title" : ""}">${escapeHtml(l)}</div>`)
    .join("");
  return new Promise((resolve) => {
    window.setTimeout(() => {
      hideChallengeRoulette();
      resolve();
    }, holdMs);
  });
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
